/**
 * IndexedDB caches and the offline outbox.
 *
 * The learner studies on a phone. Assume the connection drops mid-session:
 * any session already fetched works fully offline, and every answer goes to
 * the outbox first. Every outbox endpoint is idempotent by UUID, which is what
 * makes blind replay safe (docs/04).
 */

import { openDB, type IDBPDatabase } from "idb";
import { api, ApiError } from "./api.ts";
import type { AppState, AttemptRecord, SessionView } from "./types.ts";

const DB_NAME = "fala";
const DB_VERSION = 1;
const MAX_ATTEMPTS_BEFORE_SURFACING = 10;

interface OutboxEntry {
	id: string;
	body: AttemptRecord;
	created_at: number;
	attempts: number;
}

let dbPromise: Promise<IDBPDatabase> | undefined;

function database(): Promise<IDBPDatabase> {
	dbPromise ??= openDB(DB_NAME, DB_VERSION, {
		upgrade(db) {
			db.createObjectStore("sessions", { keyPath: "id" });
			db.createObjectStore("outbox", { keyPath: "id" });
			db.createObjectStore("meta");
		},
	});
	return dbPromise;
}

// --- caches ------------------------------------------------------------------

export async function cacheSession(session: SessionView): Promise<void> {
	(await database()).put("sessions", session);
}

export async function cachedSession(id: number): Promise<SessionView | undefined> {
	return (await database()).get("sessions", id) as Promise<SessionView | undefined>;
}

export async function cacheState(state: AppState): Promise<void> {
	(await database()).put("meta", state, "state");
}

export async function cachedState(): Promise<AppState | undefined> {
	return (await database()).get("meta", "state") as Promise<AppState | undefined>;
}

// --- outbox ------------------------------------------------------------------

type Listener = (pending: number, stuck: boolean) => void;

const listeners = new Set<Listener>();
let flushing = false;
let stuck = false;

export function onOutboxChange(listener: Listener): () => void {
	listeners.add(listener);
	void notify();
	return () => listeners.delete(listener);
}

async function notify(): Promise<void> {
	const pending = await pendingCount();
	for (const listener of listeners) listener(pending, stuck);
}

export async function pendingCount(): Promise<number> {
	return (await database()).count("outbox");
}

/** Queue an attempt and try to flush immediately. Never blocks the UI. */
export async function queueAttempt(attempt: AttemptRecord): Promise<void> {
	const db = await database();
	const entry: OutboxEntry = { id: attempt.id, body: attempt, created_at: Date.now(), attempts: 0 };
	await db.put("outbox", entry);
	await notify();
	void flushOutbox();
}

export async function flushOutbox(): Promise<void> {
	if (flushing || !navigator.onLine) return;
	flushing = true;
	try {
		const db = await database();
		const entries = (await db.getAll("outbox")) as OutboxEntry[];
		if (entries.length === 0) {
			stuck = false;
			return;
		}

		// Batch so a session's worth of answers flushes in one call.
		const batch = entries.slice(0, 50);
		try {
			await api.attempts(batch.map((entry) => entry.body));
			const tx = db.transaction("outbox", "readwrite");
			await Promise.all(batch.map((entry) => tx.store.delete(entry.id)));
			await tx.done;
			stuck = false;
		} catch (error) {
			// Never drop an entry on failure — increment and back off. After ten
			// failures, surface it rather than discarding data the learner
			// produced (docs/04).
			const permanent = error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429;
			const tx = db.transaction("outbox", "readwrite");
			for (const entry of batch) {
				const next = { ...entry, attempts: entry.attempts + 1 };
				if (permanent && next.attempts >= MAX_ATTEMPTS_BEFORE_SURFACING) {
					await tx.store.delete(entry.id);
				} else {
					await tx.store.put(next);
				}
			}
			await tx.done;
			stuck = batch.some((entry) => entry.attempts + 1 >= MAX_ATTEMPTS_BEFORE_SURFACING);
		}
	} finally {
		flushing = false;
		await notify();
	}
}

/** Flushed on `online`, on app focus, and every 30 seconds while pending. */
export function startOutboxLoop(): void {
	window.addEventListener("online", () => void flushOutbox());
	window.addEventListener("focus", () => void flushOutbox());
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") void flushOutbox();
	});
	setInterval(() => {
		void (async () => {
			if ((await pendingCount()) > 0) await flushOutbox();
		})();
	}, 30_000);
	void flushOutbox();
}

// --- local preferences -------------------------------------------------------

export function readLocal<T>(key: string, fallback: T): T {
	try {
		const raw = localStorage.getItem(`fala.${key}`);
		return raw === null ? fallback : (JSON.parse(raw) as T);
	} catch {
		return fallback;
	}
}

export function writeLocal(key: string, value: unknown): void {
	try {
		localStorage.setItem(`fala.${key}`, JSON.stringify(value));
	} catch {
		// A full or disabled storage is not worth failing a session over.
	}
}
